import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);
    
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.detail || 'Une erreur est survenue';
      throw new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
    } else {
      // Something else happened
      throw new Error('Une erreur inattendue est survenue');
    }
  }
);

// API Services
export const appointmentService = {
  // Get available time slots
  async getAvailableSlots(date = null) {
    try {
      const params = date ? { date } : {};
      const response = await apiClient.get('/appointments/slots', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching available slots:', error);
      throw error;
    }
  },

  // Create new appointment
  async createAppointment(appointmentData) {
    try {
      const response = await apiClient.post('/appointments', appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  // Get all appointments (admin)
  async getAllAppointments(statusFilter = null) {
    try {
      const params = statusFilter ? { status_filter: statusFilter } : {};
      const response = await apiClient.get('/appointments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  }
};

export const testimonialService = {
  // Get all approved testimonials
  async getTestimonials() {
    try {
      const response = await apiClient.get('/testimonials');
      return response.data;
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      throw error;
    }
  },

  // Create new testimonial (admin)
  async createTestimonial(testimonialData) {
    try {
      const response = await apiClient.post('/testimonials', testimonialData);
      return response.data;
    } catch (error) {
      console.error('Error creating testimonial:', error);
      throw error;
    }
  }
};

export const contactService = {
  // Send contact message
  async sendMessage(messageData) {
    try {
      const response = await apiClient.post('/contact', messageData);
      return response.data;
    } catch (error) {
      console.error('Error sending contact message:', error);
      throw error;
    }
  },

  // Get all contact messages (admin)
  async getAllMessages() {
    try {
      const response = await apiClient.get('/contact');
      return response.data;
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      throw error;
    }
  }
};

export const systemService = {
  // Initialize sample data
  async initializeData() {
    try {
      const response = await apiClient.post('/init-data');
      return response.data;
    } catch (error) {
      console.error('Error initializing data:', error);
      throw error;
    }
  },

  // Health check
  async healthCheck() {
    try {
      const response = await apiClient.get('/');
      return response.data;
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  }
};

// Utility functions
export const formatApiError = (error) => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  return error.message || 'Une erreur inattendue est survenue';
};

export const isApiError = (error) => {
  return error.response && error.response.status >= 400;
};

export default apiClient;